import { auth } from '../firebase'; // Import auth to get the current user's token
import { getDocumentSignedUrl } from './complianceService';

// Define API_BASE_URL once at the top level
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';

/**
 * Resolve a fresh, viewable URL for an RCTI's PDF.
 * Fetches the RCTI details (which the backend restricts to the owning
 * franchisee) and re-signs the stored PDF URL to avoid stale signed URLs,
 * mirroring how the admin app opens RCTIs.
 *
 * @param {string} rctiId - The RCTI document ID.
 * @returns {Promise<string|null>} - A signed URL to the PDF, or null if the PDF
 *   has not been generated yet.
 * @throws {Error} - If the request fails or the user lacks permission.
 */
export const getRctiPdfUrl = async (rctiId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated.');
  }

  const idToken = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}/rcti/details`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rctiId }),
  });

  const result = await response.json();

  if (!response.ok || result.status !== 'success') {
    throw new Error(result.message || 'Failed to load RCTI.');
  }

  const pdfDownloadUrl = result.data?.pdfDownloadUrl;
  if (!pdfDownloadUrl) {
    return null; // PDF not generated yet
  }

  // Re-sign to avoid stale signed URLs; fall back to the stored URL on failure.
  try {
    return await getDocumentSignedUrl(pdfDownloadUrl);
  } catch (err) {
    console.error('Failed to re-sign RCTI PDF URL, using stored URL:', err);
    return pdfDownloadUrl;
  }
};
